/**
 * Payment TanStack Query Hooks (T107)
 * Data fetching and mutations for payment management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient, { type ApiResponse } from '../lib/api-client';

// ============================================================================
// Types
// ============================================================================

export type PaymentMethod =
  | 'CREDIT_CARD'
  | 'DEBIT_CARD'
  | 'BANK_TRANSFER'
  | 'CASH'
  | 'CHECK'
  | 'APPLE_PAY'
  | 'GOOGLE_PAY'
  | 'OTHER';

export type PaymentStatus =
  | 'PENDING'
  | 'COMPLETED'
  | 'FAILED'
  | 'REFUNDED'
  | 'PARTIALLY_REFUNDED'
  | 'CANCELLED';

export interface Payment {
  id: string;
  invoiceId: string;
  invoiceNumber: string | null;
  customerId: string;
  customerName: string | null;
  customerEmail: string | null;
  amount: number;
  method: PaymentMethod;
  stripePaymentId: string | null;
  stripeAccount: 'PERSONAL' | 'YEYEO' | null;
  transactionId: string | null;
  status: PaymentStatus;
  paidAt: string | null;
  refundedAt: string | null;
  refundAmount: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  invoice?: {
    id: string;
    invoiceNumber: string;
    total: string;
    status: string;
    dueDate: string;
  };
}

export interface PaymentDetail extends Payment {
  customer: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
  };
  invoiceBalance: {
    total: number;
    totalPaid: number;
    balance: number;
  };
}

export interface PaymentsListResponse {
  payments: Payment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  summary: {
    grandTotal: number;
    byMethod: Record<string, number>;
  };
}

export interface ReconciliationResponse {
  dateFrom: string;
  dateTo: string;
  grandTotal: number;
  totalCount: number;
  byMethod: Record<
    string,
    {
      count: number;
      total: number;
      payments: Payment[];
    }
  >;
}

export interface RecordPaymentInput {
  invoiceId: string;
  amount: number;
  method: PaymentMethod;
  paymentDate?: string;
  transactionId?: string;
  notes?: string;
  overpaymentResolution?: 'apply_credit' | 'refund' | 'tip';
}

export interface UpdatePaymentInput {
  amount?: number;
  method?: PaymentMethod;
  paymentDate?: string;
  transactionId?: string;
  notes?: string;
  correctionReason: string;
}

export interface RefundInput {
  amount: number;
  reason?: string;
}

export interface PaymentsFilterParams {
  page?: number;
  limit?: number;
  customerId?: string;
  invoiceId?: string;
  status?: PaymentStatus | 'all';
  method?: PaymentMethod | 'all';
  dateFrom?: string;
  dateTo?: string;
  sort?: 'paidAt' | 'amount' | 'createdAt';
  order?: 'asc' | 'desc';
}

// ============================================================================
// Query Hooks
// ============================================================================

export function usePayments(params?: PaymentsFilterParams) {
  return useQuery({
    queryKey: ['payments', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.page) {
        searchParams.append('page', params.page.toString());
      }
      if (params?.limit) {
        searchParams.append('limit', params.limit.toString());
      }
      if (params?.customerId) {
        searchParams.append('customerId', params.customerId);
      }
      if (params?.invoiceId) {
        searchParams.append('invoiceId', params.invoiceId);
      }
      if (params?.status && params.status !== 'all') {
        searchParams.append('status', params.status);
      }
      if (params?.method && params.method !== 'all') {
        searchParams.append('method', params.method);
      }
      if (params?.dateFrom) {
        searchParams.append('dateFrom', params.dateFrom);
      }
      if (params?.dateTo) {
        searchParams.append('dateTo', params.dateTo);
      }
      if (params?.sort) {
        searchParams.append('sort', params.sort);
      }
      if (params?.order) {
        searchParams.append('order', params.order);
      }

      const response = await apiClient.get<ApiResponse<PaymentsListResponse>>(
        `/payments?${searchParams.toString()}`
      );
      return response.data.data;
    },
  });
}

export function usePayment(paymentId: string) {
  return useQuery({
    queryKey: ['payment', paymentId],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<PaymentDetail>>(`/payments/${paymentId}`);
      return response.data.data;
    },
    enabled: !!paymentId,
  });
}

export function useReconciliation(params: {
  dateFrom: string;
  dateTo: string;
  method?: PaymentMethod;
}) {
  return useQuery({
    queryKey: ['reconciliation', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      searchParams.append('dateFrom', params.dateFrom);
      searchParams.append('dateTo', params.dateTo);
      if (params.method) {
        searchParams.append('method', params.method);
      }

      const response = await apiClient.get<ApiResponse<ReconciliationResponse>>(
        `/payments/reconciliation?${searchParams.toString()}`
      );
      return response.data.data;
    },
    enabled: !!params.dateFrom && !!params.dateTo,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useRecordPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RecordPaymentInput) => {
      const response = await apiClient.post<ApiResponse<Payment>>('/payments', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['reconciliation'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdatePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdatePaymentInput }) => {
      const response = await apiClient.put<ApiResponse<Payment>>(`/payments/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['payment', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['reconciliation'] });
    },
  });
}

export function useRefundPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: RefundInput }) => {
      const response = await apiClient.post<ApiResponse<Payment>>(`/payments/${id}/refund`, data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['payment', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['reconciliation'] });
    },
  });
}

export function useDeletePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.delete<ApiResponse<null>>(`/payments/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useExportPaymentsCSV() {
  return useMutation({
    mutationFn: async (params: { dateFrom: string; dateTo: string; method?: PaymentMethod }) => {
      const searchParams = new URLSearchParams();
      searchParams.append('dateFrom', params.dateFrom);
      searchParams.append('dateTo', params.dateTo);
      if (params.method) {
        searchParams.append('method', params.method);
      }

      const response = await apiClient.get(`/payments/export?${searchParams.toString()}`, {
        responseType: 'blob',
      });
      return {
        blob: response.data as Blob,
        filename: `payments-${params.dateFrom}-to-${params.dateTo}.csv`,
      };
    },
    onSuccess: ({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
}

// ============================================================================
// Helpers
// ============================================================================

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  CREDIT_CARD: 'Credit Card',
  DEBIT_CARD: 'Debit Card',
  BANK_TRANSFER: 'Bank Transfer',
  CASH: 'Cash',
  CHECK: 'Check',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  OTHER: 'Other',
};

export const PAYMENT_STATUS_CONFIG: Record<PaymentStatus, { label: string; className: string }> = {
  PENDING: { label: 'Pending', className: 'claude-badge claude-badge-warning' },
  COMPLETED: { label: 'Completed', className: 'claude-badge claude-badge-success' },
  FAILED: { label: 'Failed', className: 'claude-badge claude-badge-error' },
  REFUNDED: { label: 'Refunded', className: 'claude-badge claude-badge-default' },
  PARTIALLY_REFUNDED: { label: 'Partial Refund', className: 'claude-badge claude-badge-warning' },
  CANCELLED: { label: 'Cancelled', className: 'claude-badge claude-badge-default' },
};

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function formatDate(dateString: string | null): string {
  if (!dateString) {
    return '—';
  }
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
